import React, { useContext, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import Banner from '@toasttab/shared/src/components/organisms/Header/Banner/Banner'
import RegionBanner from './RegionBanner'
import GlobalBanner from './Banner/GlobalBanner'
import MobileStickyCta from '@toasttab/shared/src/components/organisms//Navigation/MobileStickyCta'
import { useScrollPosition } from '@toasttab/shared/src/hooks/useScrollPosition'
import { usePrevious } from '@toasttab/shared/src/hooks/usePrevious'
import mobileSalesScheduleData from '@toasttab/main/src/data/MobileSalesIndicatorSchedule.json'
import NavController from '@toasttab/shared/src/components/organisms/Navigation/NavController'
import { OTLContext } from '@toasttab/shared/src/context/OTLContext'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import { HeaderProps } from '@toasttab/shared/src/components/organisms/Header/Header.d'
import RegionSwitcher from './RegionSwitcher'
import useRegionSwitcherRedesign from '@toasttab/shared/src/utils/featureFlags'

const Header = ({
  enabledMobileStickyCTA = false,
  localizedEntries,
  mobileSalesIndicatorSchedule,
  packageName,
  siteId,
  siteMetadata
}: HeaderProps) => {
  const [readingProgress, setReadingProgress] = useState(0)
  const [readingMax, setReadingMax] = useState(0)
  const [isHidden, setIsHidden] = useState(false)

  const scrollPosition = useScrollPosition()
  const pageContext = usePageContext()
  const headerStyle = pageContext?.headerStyle || 'Default'
  const otlParams = useContext(OTLContext)
  const OTLArticleUX: boolean = Boolean(otlParams.isArticle)
  const ref = useRef<any | null>(null)

  // Store the previous scroll value so we can determine if the user is scrolling up or down to show or hide the nav
  const prevScroll = usePrevious(scrollPosition)

  if (!mobileSalesIndicatorSchedule) {
    mobileSalesIndicatorSchedule =
      mobileSalesScheduleData.mobileSalesIndicatorSchedule
  }

  useEffect(() => {
    if (OTLArticleUX) {
      const trackScrollPosition = () => {
        let winHeight: any = window.innerHeight,
          docHeight: any = document.documentElement.scrollHeight,
          max: any

        // Set the max scrollable area
        max = docHeight - winHeight

        setReadingMax(max)
        setReadingProgress(scrollPosition)
      }

      document.addEventListener('scroll', function () {
        trackScrollPosition()
      })
    }
  }, [scrollPosition])

  useEffect(() => {
    if (!OTLArticleUX && siteId !== 1 && prevScroll) {
      const headerHeight = ref.current.clientHeight
      // Slide header up if user is scrolling down
      if (!isHidden && scrollPosition > 50 && prevScroll < scrollPosition) {
        ref.current.style.top = `-${headerHeight}px`
        setIsHidden(true)
        // Slide header back down into view if user is scrolling up
      } else if (
        isHidden &&
        (prevScroll > scrollPosition || scrollPosition === 0)
      ) {
        ref.current.style.top = '0'
        setIsHidden(false)
      }
    }
  }, [prevScroll, scrollPosition, isHidden])

  const ctas = [
    {
      text: 'Adapting to COVID-19',
      link: '/blog/on-the-line/covid-19/'
    },
    {
      text: 'Going Digital',
      link: '/blog/on-the-line/online-ordering-for-restaurants/'
    }
  ]

  const headerClasses = classNames({
    'fixed top-0 w-full z-50 bg-white transition-all':
      headerStyle !== 'Logo Only',
    'shadow-[rgba(0,0,0,0.08)]': OTLArticleUX && scrollPosition > 100,
    'shadow-[rgba(0,0,0,0)]': !OTLArticleUX || scrollPosition <= 100,
    'shadow-nav duration-300': OTLArticleUX,
    'duration-1000': !OTLArticleUX
  })

  return (
    <header className={headerClasses} ref={ref}>
      {['main', 'onTheLine'].includes(packageName) &&
        (useRegionSwitcherRedesign ? (
          <RegionSwitcher
            siteId={siteId}
            packageName={packageName}
            localizedEntries={localizedEntries}
          />
        ) : (
          <RegionBanner
            siteId={siteId}
            packageName={packageName}
            localizedEntries={localizedEntries}
          />
        ))}
      <NavController
        mobileSalesIndicatorSchedule={mobileSalesIndicatorSchedule}
        packageName={packageName}
        siteId={siteId}
        siteMetadata={siteMetadata}
      />
      {['onTheLine', 'main'].includes(packageName) && <GlobalBanner />}
      {<Banner title={'Coronavirus'} ctas={ctas} disabled />}
      {siteId === 1 &&
        !['support'].includes(packageName) &&
        enabledMobileStickyCTA && <MobileStickyCta />}
      {OTLArticleUX && (
        <progress value={readingProgress} max={readingMax}></progress>
      )}
    </header>
  )
}

export default Header
